/** Translation bindings in the tag data editor (backend metaplan A6, AIE §8 / §10).
 *
 *  A binding makes one language leaf of a multilingual attribute (the TARGET) the machine
 *  translation of another leaf (the ORIGIN); editing the origin re-translates its targets.
 *  The editor marks bound leaves (editor metadata, never content) and, when a bound TARGET
 *  leaf is edited, asks whether to keep the binding (the edit is replaced at the next origin
 *  change) or unbind (the default). */
import type { TranslationBinding } from '@/api/tags';

export interface LeafBindings {
  /** The binding this leaf is the target of (one origin per target). */
  asTarget?: TranslationBinding;
  /** Bindings this leaf feeds. */
  asOrigin: TranslationBinding[];
}

/** `{attribute: {lang: LeafBindings}}` for the top-level multilingual attributes of this tag. */
export type BindingsByAttribute = Record<string, Record<string, LeafBindings>>;

function topLevelAttribute(pointer: string): string | null {
  if (!pointer.startsWith('/')) return null;
  const tokens = pointer.slice(1).split('/');
  if (tokens.length !== 1 || !tokens[0]) return null;
  return tokens[0].replace(/~1/g, '/').replace(/~0/g, '~');
}

export function bindingsByAttribute(bindings: TranslationBinding[] | undefined): BindingsByAttribute {
  const out: BindingsByAttribute = {};
  for (const b of bindings ?? []) {
    const isTarget = b.role === 'target';
    const attribute = topLevelAttribute(isTarget ? b.target_path : b.origin_path);
    if (!attribute) continue;
    const lang = isTarget ? b.target_lang : b.origin_lang;
    const byLang = (out[attribute] ??= {});
    const leaf = (byLang[lang] ??= { asOrigin: [] });
    if (isTarget) leaf.asTarget = b;
    else leaf.asOrigin.push(b);
  }
  return out;
}

function leafText(data: Record<string, unknown>, attribute: string, lang: string): string | undefined {
  const value = data[attribute];
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const leaf = (value as Record<string, unknown>)[lang];
  return leaf == null ? undefined : String(leaf);
}

/** Target bindings whose leaf the draft changes (edited or removed) — the ones to ask about. */
export function changedBoundTargets(
  bindings: TranslationBinding[] | undefined,
  baseline: Record<string, unknown>,
  draft: Record<string, unknown>,
): TranslationBinding[] {
  return (bindings ?? []).filter((b) => {
    if (b.role !== 'target') return false;
    const attribute = topLevelAttribute(b.target_path);
    if (!attribute) return false;
    return leafText(baseline, attribute, b.target_lang) !== leafText(draft, attribute, b.target_lang);
  });
}
